import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetReadOnly } from '../SettingsShell/rows';
import { SetSwitch } from '../SettingsShell/controls';
import { GAP_FILLER_SETTINGS, gapRulesStore, reorderGapRules, toggleGapRule } from '../../settings-gapfiller/mockData';
import type { GapRule } from '../../settings-gapfiller/types';

export interface GapFillerSettingsViewProps {
  onAddRule?: () => void;
  onEditRule?: (id: string) => void;
  onEditOffer?: () => void;
  /** Card 05 — shorten a rule's minimum, or switch a rule on. Both work. */
  onShortenRule?: (id: string) => void;
  onSwitchOnRule?: (id: string) => void;
}

/**
 * Settings › AI Brain › Gap Filler — **rebuilt** (amendment 26 §4).
 *
 * Removed, all of it: priority numbers, the three outreach modes, the
 * channel-order control, *Respect quiet hours*, travel distance, *How the list is
 * ordered*, the balance paragraph, the quiet-hours history box and every
 * explanatory footer. Order is the position in the list; nothing else sets it.
 */
export function GapFillerSettingsView({
  onAddRule,
  onEditRule,
  onEditOffer,
  onShortenRule,
  onSwitchOnRule,
}: GapFillerSettingsViewProps) {
  const d = GAP_FILLER_SETTINGS;
  const [, force] = useState(0);
  const rerender = () => { force((n) => n + 1); };
  const rules = gapRulesStore.rules;
  const rulesOn = rules.filter((r) => r.enabled).length;

  return (
    <div className="min-w-0 flex-1">
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="Gap"
        titleAccent="Filler"
        sub="When a chair comes free, ELVA offers it to the patients most likely to take it, and books the first one who says yes."
      />

      <SettingsCards>
        {/* 01 — which gaps */}
        <SettingsCard n="01" title="Which gaps ELVA looks for" sub="Free chair time it finds in the schedule. Staff can also add a gap by hand.">
          <SettingsRow label="Look ahead" desc="How far into the schedule ELVA looks for free time." control={<Stepper value={d.whichGaps.lookAheadDays} unit="days" />} />
          <SettingsRow label="Shortest gap" desc="Anything shorter is left alone. Each rule can ask for longer." control={<Stepper value={d.whichGaps.shortestGapMinutes} unit="minutes" />} />
          <SettingsRow label="A no-show needs at least" desc="Time left in the slot once the patient hasn’t arrived." control={<Stepper value={d.whichGaps.noShowMinLeft} unit="minutes left" />} />
          <SettingsRow last label="Short gaps between appointments" desc="Free time squeezed between two bookings." control={<SetSwitch label="Short gaps between appointments" checked={d.whichGaps.shortGapsBetween} onChange={() => undefined} />} />
        </SettingsCard>

        {/* 02 — who gets offered a gap */}
        <SettingsCard
          n="02"
          title="Who gets offered a gap"
          sub="Rules higher up are asked first. Drag to change the order."
          headRight={<Button variant="primary" size="sm" onClick={onAddRule}>Add a rule</Button>}
        >
          {rules.map((r, i) => (
            <RuleRow
              key={r.id}
              rule={r}
              index={i}
              last={i === rules.length - 1}
              onEdit={() => { onEditRule?.(r.id); }}
              onToggle={(on) => { toggleGapRule(r.id, on); rerender(); }}
              onMove={(dir) => {
                const to = i + dir;
                if (to >= 0 && to < rules.length) { reorderGapRules(i, to); rerender(); }
              }}
            />
          ))}
          <SettingsNote>
            {rules.length} rules · {rulesOn} on · A patient in two rules is offered a gap once.
          </SettingsNote>
        </SettingsCard>

        {/* 03 — how offers go out */}
        <SettingsCard n="03" title="How offers go out" sub="A few patients at a time. The first one who says yes gets it, and everyone else is told it’s gone.">
          <SettingsRow label="How many at once" desc="Set it to 1 to ask one patient at a time." control={<Stepper value={d.offer.howManyAtOnce} unit="patients" />} />
          <SettingsRow label="Then wait" desc="Before the next patients are asked, if the gap is still open." control={<Stepper value={d.offer.thenWaitMinutes} unit="minutes" />} />
          <SettingsRow label="Stop after" desc="Nobody more is asked once this many have been." control={<Stepper value={d.offer.stopAfterPatients} unit="patients" />} />
          <SettingsRow label="Offers expire" desc="A patient who says yes after this is told it’s gone." control={<SetReadOnly value={d.offer.expiry} />} />
          <SettingsRow label="The offer" desc="What each patient receives, and in what order.">
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {d.offer.offerChips.map((chip) => (
                <span key={chip} className="rounded-[7px] bg-[var(--surface-sunken)] px-2 py-1 text-[11.5px] text-[var(--text-secondary)]">
                  {chip}
                </span>
              ))}
              <button type="button" onClick={onEditOffer} className="ml-1 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                Edit messages
              </button>
            </div>
          </SettingsRow>
          <SettingsRow
            last
            label="Send after hours for a gap in the next 24 hours"
            desc="Otherwise offers wait for sending hours, set on Channels."
            control={<SetSwitch label="Send after hours for a gap in the next 24 hours" checked={d.offer.sendAfterHoursWithin24h} onChange={() => undefined} />}
          />
        </SettingsCard>

        {/* 04 — never offered a gap */}
        <SettingsCard n="04" title="Never offered a gap" sub="Checked for every patient, every time. Not settings.">
          {d.neverOffered.map((line, i) => (
            <div
              key={line}
              className="px-6 py-3 text-[13px] text-[var(--text-secondary)]"
              style={i === d.neverOffered.length - 1 ? undefined : { borderBottom: '1px solid #edf0f6' }}
            >
              {line}
            </div>
          ))}
        </SettingsCard>

        {/* 05 — gaps nobody could be offered */}
        <SettingsCard n="05" accent="amber" title="Gaps nobody could be offered" sub={`${String(d.uncovered.length)} of 112 in the last 30 days. Nobody was asked, and the time went empty.`}>
          {d.uncovered.map((u, i) => (
            <SettingsRow
              key={u.id}
              last={i === d.uncovered.length - 1}
              label={u.what}
              desc={u.reason}
              control={
                <button
                  type="button"
                  onClick={() => {
                    if (u.fix.kind === 'shorten') onShortenRule?.(u.fix.ruleId);
                    else { onSwitchOnRule?.(u.fix.ruleId); rerender(); }
                  }}
                  className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
                >
                  {u.fix.label}
                </button>
              }
            />
          ))}
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location="Settings · AI Brain · Gap Filler" />
    </div>
  );
}

function Stepper({ value, unit }: { value: number; unit: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex h-8 min-w-[48px] items-center justify-center rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2.5 font-[var(--font-mono)] text-[14px] font-bold text-[var(--text-primary)]">
        {value}
      </span>
      <span className="text-[12.5px] text-[var(--text-muted)]">{unit}</span>
    </span>
  );
}

function RuleRow({
  rule,
  index,
  last,
  onEdit,
  onToggle,
  onMove,
}: {
  rule: GapRule;
  index: number;
  last: boolean;
  onEdit: () => void;
  onToggle: (on: boolean) => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const gapLine = `${rule.providers} · ${String(rule.shortestGapMinutes)} min+${rule.window !== undefined ? ` · ${rule.window.days.join(' ')}${rule.window.afterTime !== undefined ? ` after ${rule.window.afterTime}` : ''}` : ''}`;
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3.5"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      {/* Drag is a real reorder — up/down buttons stand in for the handle so it
          works with a keyboard too, and both write the store (§0). */}
      <span className="flex shrink-0 flex-col text-[var(--text-muted)]">
        <button type="button" aria-label="Move up" onClick={() => { onMove(-1); }} className="hover:text-[var(--brand-indigo)]">
          <Icon kind="up" size={12} stroke={2.2} />
        </button>
        <button type="button" aria-label="Move down" onClick={() => { onMove(1); }} className="hover:text-[var(--brand-indigo)]">
          <Icon kind="down" size={12} stroke={2.2} />
        </button>
      </span>
      <span className="w-5 shrink-0 font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-muted)]">{index + 1}</span>
      <span className="min-w-[200px] flex-1">
        <span className="flex items-center gap-1.5">
          <span className="text-[13.5px] font-semibold text-[var(--text-primary)]">{rule.name}</span>
          {rule.custom ? (
            <span className="rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--brand-indigo)]">
              Custom
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 block text-[11.5px] text-[var(--text-muted)]">{rule.who}</span>
        <span className="mt-0.5 block text-[11.5px] text-[var(--text-secondary)]">
          {rule.bookInto} · {gapLine}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">{rule.patients}</span>
        <span className="block text-[10.5px] text-[var(--text-muted)]">patients</span>
      </span>
      <button type="button" onClick={onEdit} className="shrink-0 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
        Edit
      </button>
      <SetSwitch label={rule.name} checked={rule.enabled} onChange={onToggle} />
    </div>
  );
}

export type { IconKind };
